import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const userData = await authService.getCurrentUser(storedToken);
          setUser(userData);
          setToken(storedToken);
          // Check if user must change password
          setMustChangePassword(userData?.must_change_password || false);
        } catch (error) {
          console.error('Failed to restore session:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      // Handle MFA required response
      if (response.mfa_required) {
        return { 
          success: false, 
          mfaRequired: true,
          userId: response.user_id,
          message: response.message || 'MFA verification required'
        };
      }
      
      // Standard login success
      const { access_token, user: userData, must_change_password } = response;
      
      if (!access_token) {
        return { 
          success: false, 
          error: 'No access token received' 
        };
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData || null);
      setMustChangePassword(must_change_password || false);
      
      // If user data not in response, derive role from JWT
      let userRole = userData?.role;
      if (!userRole && access_token) {
        try {
          const payload = JSON.parse(atob(access_token.split('.')[1]));
          userRole = payload.role;
        } catch (e) {
          console.warn('Could not decode JWT for role');
        }
      }
      
      return { 
        success: true, 
        user: userData || { role: userRole },
        role: userRole,
        mustChangePassword: must_change_password || false
      };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const verifyMfa = async (userId, mfaCode) => {
    try {
      const response = await authService.verifyMfa(userId, mfaCode);
      const { access_token, user: userData, must_change_password } = response;
      
      if (!access_token) {
        return { 
          success: false, 
          error: 'No access token received' 
        };
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData || null);
      setMustChangePassword(must_change_password || false);
      
      // Derive role from JWT if not in user data
      let userRole = userData?.role;
      if (!userRole && access_token) {
        try {
          const payload = JSON.parse(atob(access_token.split('.')[1]));
          userRole = payload.role;
        } catch (e) {
          console.warn('Could not decode JWT for role');
        }
      }
      
      return { 
        success: true, 
        user: userData || { role: userRole },
        role: userRole,
        mustChangePassword: must_change_password || false
      };
    } catch (error) {
      console.error('MFA verification failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'MFA verification failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const { access_token, user: newUser } = response;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      setMustChangePassword(false);
      
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  const value = {
    user,
    token,
    loading,
    login,
    verifyMfa,
    register,
    logout,
    isAuthenticated: !!token,
    mustChangePassword,
    clearMustChangePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
